import React, { useState, useEffect } from 'react'
import { Search, X } from 'lucide-react'
import { useDebounce } from '@hooks'
import './SearchBar.css'

/**
 * SearchBar Component
 *
 * A reusable search input with debouncing, clear functionality, and loading state
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Current search value
 * @param {Function} props.onChange - Change handler (receives debounced value)
 * @param {string} props.placeholder - Placeholder text
 * @param {number} props.debounceMs - Debounce delay in milliseconds (default: 500)
 * @param {boolean} props.loading - Loading state
 * @param {string} props.className - Additional CSS classes
 */
const SearchBar = ({
	value = '',
	onChange,
	placeholder = 'Search...',
	debounceMs = 500,
	loading = false,
	className = '',
}) => {
	const [localValue, setLocalValue] = useState(value)
	const debouncedValue = useDebounce(localValue, debounceMs)

	// Sync with external value changes
	useEffect(() => {
		setLocalValue(value)
	}, [value])

	// Trigger onChange when debounced value changes
	useEffect(() => {
		if (debouncedValue !== value) {
			onChange(debouncedValue)
		}
	}, [debouncedValue])

	/**
	 * Handle input change
	 */
	const handleChange = (e) => {
		setLocalValue(e.target.value)
	}

	/**
	 * Clear search input
	 */
	const handleClear = () => {
		setLocalValue('')
		onChange('')
	}

	const wrapperClasses = ['search-bar', className].filter(Boolean).join(' ')

	return (
		<div className={wrapperClasses}>
			<div className="search-bar__icon">
				<Search size={18} />
			</div>
			<input
				type="text"
				className="search-bar__input"
				placeholder={placeholder}
				value={localValue}
				onChange={handleChange}
				aria-label="Search"
			/>
			{loading && (
				<div className="search-bar__loading">
					<div className="loading-spinner"></div>
				</div>
			)}
			{localValue && !loading && (
				<button
					className="search-bar__clear"
					onClick={handleClear}
					aria-label="Clear search"
				>
					<X size={18} />
				</button>
			)}
		</div>
	)
}

export default SearchBar
